import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Auth } from 'aws-amplify';
import { useNavigate } from 'react-router-dom';

import AdminNavTabs from '../components/AdminNavTabs';
import './AdminDashboard.css';

const PROJECT_NAME = process.env.REACT_APP_PROJECT_NAME || 'AppDemo';
const API_BASE_URL = process.env.REACT_APP_ADMIN_API_URL || process.env.REACT_APP_API_URL || '';

const centsToDollars = (cents) => {
  const n = Number(cents);
  if (!Number.isFinite(n)) return '';
  return (n / 100).toFixed(2);
};

const dollarsToCents = (dollars) => {
  const n = Number(dollars);
  if (!Number.isFinite(n)) return null;
  return Math.round(n * 100);
};

const fallbackDefaults = {
  shipping: {
    standard: { enabled: true, label: 'Standard Shipping', amount_cents: 499, min_days: 3, max_days: 5, currency: 'usd' },
    express: { enabled: false, label: 'Express Shipping', amount_cents: 999, min_days: 1, max_days: 2, currency: 'usd' },
    free: { enabled: true, label: 'Free Shipping', threshold_cents: 2000, amount_cents: 0, min_days: 5, max_days: 8, currency: 'usd' },
    allowed_countries: ['US'],
  },
  tax: { stripe_automatic_tax_enabled: false },
  optional_sections: {
    crochet_features: {
      enabled: false,
      title: 'Crochet features',
      html:
        '<div class="crochet-features">' +
        '<ul class="feature-bullets">' +
        '<li><strong>Handmade look, everyday comfort:</strong> soft texture with a cozy feel.</li>' +
        '<li><strong>Thoughtful details:</strong> carefully finished edges and sturdy stitching.</li>' +
        '<li><strong>Gift-ready favorites:</strong> perfect for birthdays, holidays, and treat-yourself moments.</li>' +
        '<li><strong>Limited batches:</strong> crafted in small runs so each piece feels special.</li>' +
        '</ul>' +
        '<p><a class="feature-cta" href="/products">Shop the collection</a></p>' +
        '</div>',
    },
    events: { enabled: false, title: 'Events', html: '' },
    reviews: { enabled: false, title: 'Reviews', auto_approve: false, double_opt_in: true },
  },
};

export default function GeneralSettings() {
  const navigate = useNavigate();

  const statusRef = useRef(null);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [username, setUsername] = useState('');

  const [error, setError] = useState('');
  const [statusMessage, setStatusMessage] = useState('');

  const [source, setSource] = useState('legacy');

  const [form, setForm] = useState(() => fallbackDefaults);

  const legacyBanner = useMemo(() => source === 'legacy', [source]);

  useEffect(() => {
    const init = async () => {
      try {
        const user = await Auth.currentAuthenticatedUser();
        const session = await Auth.currentSession();
        const groups = session.getIdToken().payload['cognito:groups'] || [];

        if (!groups.includes('Admins')) {
          navigate('/admin/login');
          return;
        }

        setUsername(user.username || user.attributes?.email || 'Admin');
        await fetchSettings();
      } catch (e) {
        navigate('/admin/login');
      } finally {
        setLoading(false);
      }
    };

    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate]);

  const fetchSettings = async () => {
    if (!API_BASE_URL) return;
    try {
      setError('');
      setStatusMessage('');

      const session = await Auth.currentSession();
      const token = session.getIdToken().getJwtToken();

      const res = await fetch(`${API_BASE_URL}/admin/general`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || 'Failed to load general settings');

      const incoming = data?.settings && typeof data.settings === 'object' ? data.settings : null;
      if (incoming) {
        setForm(incoming);
      } else {
        setForm(fallbackDefaults);
      }
      setSource(data?.source || 'legacy');
    } catch (e) {
      setError(e?.message || 'Failed to load settings');
      setForm(fallbackDefaults);
      setSource('legacy');
    }
  };

  const updateShipping = (segment, patch) => {
    setForm((prev) => {
      const shipping = { ...(prev.shipping || {}) };
      const seg = { ...(shipping[segment] || {}) };
      shipping[segment] = { ...seg, ...patch };
      return { ...prev, shipping };
    });
  };

  const updateTax = (patch) => {
    setForm((prev) => ({ ...prev, tax: { ...(prev.tax || {}), ...patch } }));
  };

  const updateOptionalCrochetFeatures = (patch) => {
    setForm((prev) => {
      const optional_sections = { ...(prev.optional_sections || {}) };
      const crochet_features = { ...(optional_sections.crochet_features || fallbackDefaults.optional_sections.crochet_features) };
      optional_sections.crochet_features = { ...crochet_features, ...patch };
      return { ...prev, optional_sections };
    });
  };

  const updateOptionalEvents = (patch) => {
    setForm((prev) => {
      const optional_sections = { ...(prev.optional_sections || {}) };
      const events = { ...(optional_sections.events || fallbackDefaults.optional_sections.events) };
      optional_sections.events = { ...events, ...patch };
      return { ...prev, optional_sections };
    });
  };

  const updateOptionalReviews = (patch) => {
    setForm((prev) => {
      const optional_sections = { ...(prev.optional_sections || {}) };
      const reviews = { ...(optional_sections.reviews || fallbackDefaults.optional_sections.reviews) };
      optional_sections.reviews = { ...reviews, ...patch };
      return { ...prev, optional_sections };
    });
  };

  const save = async () => {
    if (!API_BASE_URL) return;
    try {
      setSaving(true);
      setError('');
      setStatusMessage('');

      const session = await Auth.currentSession();
      const token = session.getIdToken().getJwtToken();

      const payload = {
        settings: {
          shipping: {
            allowed_countries: form?.shipping?.allowed_countries || ['US'],
            standard: {
              enabled: Boolean(form?.shipping?.standard?.enabled),
              label: String(form?.shipping?.standard?.label || ''),
              amount_cents: dollarsToCents(form?.shipping?.standard?.amount_dollars ?? centsToDollars(form?.shipping?.standard?.amount_cents)) ?? 499,
              min_days: Number(form?.shipping?.standard?.min_days ?? 3),
              max_days: Number(form?.shipping?.standard?.max_days ?? 5),
            },
            express: {
              enabled: Boolean(form?.shipping?.express?.enabled),
              label: String(form?.shipping?.express?.label || ''),
              amount_cents: dollarsToCents(form?.shipping?.express?.amount_dollars ?? centsToDollars(form?.shipping?.express?.amount_cents)) ?? 999,
              min_days: Number(form?.shipping?.express?.min_days ?? 1),
              max_days: Number(form?.shipping?.express?.max_days ?? 2),
            },
            free: {
              enabled: Boolean(form?.shipping?.free?.enabled),
              label: String(form?.shipping?.free?.label || ''),
              threshold_cents: dollarsToCents(form?.shipping?.free?.threshold_dollars ?? centsToDollars(form?.shipping?.free?.threshold_cents)) ?? 2000,
              min_days: Number(form?.shipping?.free?.min_days ?? 5),
              max_days: Number(form?.shipping?.free?.max_days ?? 8),
            },
          },
          tax: {
            stripe_automatic_tax_enabled: Boolean(form?.tax?.stripe_automatic_tax_enabled),
          },
          optional_sections: {
            crochet_features: {
              enabled: Boolean(form?.optional_sections?.crochet_features?.enabled),
              title: String(form?.optional_sections?.crochet_features?.title || 'Crochet features'),
              html: String(form?.optional_sections?.crochet_features?.html || ''),
            },
            events: {
              enabled: Boolean(form?.optional_sections?.events?.enabled),
              title: String(form?.optional_sections?.events?.title || 'Events'),
              html: String(form?.optional_sections?.events?.html || ''),
            },
            reviews: {
              enabled: Boolean(form?.optional_sections?.reviews?.enabled),
              title: String(form?.optional_sections?.reviews?.title || 'Reviews'),
              auto_approve: Boolean(form?.optional_sections?.reviews?.auto_approve),
              double_opt_in: true,
            },
          },
        },
      };

      const res = await fetch(`${API_BASE_URL}/admin/general`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || 'Save failed');

      setForm(data?.settings || form);
      setSource(data?.source || 'stored');
      const stripeSyncOk = Boolean(data?.stripeSync?.ok);
      const stripeSyncErr = data?.stripeSync?.error;
      if (stripeSyncOk) {
        setStatusMessage('Saved.');
      } else if (stripeSyncErr) {
        setStatusMessage(`Saved. Shipping rates could not be synced: ${stripeSyncErr}`);
      } else {
        setStatusMessage('Saved. Shipping rates could not be synced.');
      }
    } catch (e) {
      setError(e?.message || 'Save failed');
    } finally {
      try {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } catch {
        window.scrollTo(0, 0);
      }
      setTimeout(() => {
        try {
          statusRef.current?.focus?.();
          statusRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
        } catch {
          // ignore
        }
      }, 50);
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="admin-loading">
        <i className="fas fa-spinner fa-spin"></i>
        <p>Loading settings...</p>
      </div>
    );
  }

  const standard = form?.shipping?.standard || fallbackDefaults.shipping.standard;
  const express = form?.shipping?.express || fallbackDefaults.shipping.express;
  const free = form?.shipping?.free || fallbackDefaults.shipping.free;

  return (
    <div className="admin-dashboard">
      <header className="admin-header">
        <div className="admin-header-left">
          <div className="admin-header-logo">
            <i className="fas fa-sliders-h"></i>
            <span>{PROJECT_NAME}</span>
          </div>
          <div>
            <h1>General</h1>
            <p>Shipping & taxes</p>
          </div>
        </div>
        <div className="admin-header-right">
          <AdminNavTabs />
          <span className="admin-user">
            <i className="fas fa-user-shield"></i>
            {username || 'Admin'}
          </span>
          <button onClick={() => Auth.signOut().then(() => navigate('/admin/login'))} className="btn-logout">
            <i className="fas fa-sign-out-alt"></i>
            Logout
          </button>
        </div>
      </header>

      <div className="admin-content">
        {legacyBanner ? (
          <div className="alert" style={{ background: '#fff7ed', border: '1px solid #fed7aa', color: '#9a3412' }}>
            <i className="fas fa-info-circle" style={{ marginRight: 8 }}></i>
            Currently using legacy defaults: Standard $4.99, Free over $20. Save to start managing shipping here.
          </div>
        ) : null}

        {error ? (
          <div className="alert alert-error" ref={statusRef} tabIndex={-1} role="alert" aria-live="assertive">
            <i className="fas fa-exclamation-circle"></i>
            {error}
          </div>
        ) : null}

        {statusMessage ? (
          <div className="alert alert-success" ref={statusRef} tabIndex={-1} role="status" aria-live="polite">
            <i className="fas fa-check-circle"></i>
            {statusMessage}
          </div>
        ) : null}

        <div className="products-list-container">
          <details open>
            <summary style={{ cursor: 'pointer', fontWeight: 900, fontSize: '1.2rem', color: '#4c1d95' }}>Shipping rates</summary>

          <div style={{ display: 'grid', gap: 18, marginTop: 14 }}>
            <section style={{ background: '#fff', borderRadius: 16, padding: 18, border: '1px solid rgba(148, 163, 184, 0.35)' }}>
              <h3 style={{ marginBottom: 12 }}>Standard shipping</h3>
              <label className="toggle-chip" style={{ display: 'inline-flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
                <input
                  type="checkbox"
                  checked={Boolean(standard.enabled)}
                  onChange={(e) => updateShipping('standard', { enabled: e.target.checked })}
                />
                Enabled
              </label>

              <div className="form-row" style={{ gap: 14 }}>
                <div className="form-group">
                  <label>Label</label>
                  <input
                    value={standard.label || ''}
                    onChange={(e) => updateShipping('standard', { label: e.target.value })}
                    placeholder="Standard Shipping"
                  />
                </div>
                <div className="form-group">
                  <label>Price (USD)</label>
                  <input
                    value={centsToDollars(standard.amount_cents)}
                    onChange={(e) => updateShipping('standard', { amount_cents: dollarsToCents(e.target.value) ?? standard.amount_cents })}
                    placeholder="4.99"
                  />
                </div>
              </div>

              <div className="form-row" style={{ gap: 14 }}>
                <div className="form-group">
                  <label>Min days</label>
                  <input
                    type="number"
                    value={standard.min_days ?? 3}
                    onChange={(e) => updateShipping('standard', { min_days: Number(e.target.value) })}
                  />
                </div>
                <div className="form-group">
                  <label>Max days</label>
                  <input
                    type="number"
                    value={standard.max_days ?? 5}
                    onChange={(e) => updateShipping('standard', { max_days: Number(e.target.value) })}
                  />
                </div>
              </div>
            </section>

            <section style={{ background: '#fff', borderRadius: 16, padding: 18, border: '1px solid rgba(148, 163, 184, 0.35)' }}>
              <h3 style={{ marginBottom: 12 }}>Express shipping</h3>
              <label className="toggle-chip" style={{ display: 'inline-flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
                <input
                  type="checkbox"
                  checked={Boolean(express.enabled)}
                  onChange={(e) => updateShipping('express', { enabled: e.target.checked })}
                />
                Enabled
              </label>

              <div className="form-row" style={{ gap: 14 }}>
                <div className="form-group">
                  <label>Label</label>
                  <input
                    value={express.label || ''}
                    onChange={(e) => updateShipping('express', { label: e.target.value })}
                    placeholder="Express Shipping"
                  />
                </div>
                <div className="form-group">
                  <label>Price (USD)</label>
                  <input
                    value={centsToDollars(express.amount_cents)}
                    onChange={(e) => updateShipping('express', { amount_cents: dollarsToCents(e.target.value) ?? express.amount_cents })}
                    placeholder="9.99"
                    disabled={!express.enabled}
                  />
                </div>
              </div>

              <div className="form-row" style={{ gap: 14 }}>
                <div className="form-group">
                  <label>Min days</label>
                  <input
                    type="number"
                    value={express.min_days ?? 1}
                    onChange={(e) => updateShipping('express', { min_days: Number(e.target.value) })}
                    disabled={!express.enabled}
                  />
                </div>
                <div className="form-group">
                  <label>Max days</label>
                  <input
                    type="number"
                    value={express.max_days ?? 2}
                    onChange={(e) => updateShipping('express', { max_days: Number(e.target.value) })}
                    disabled={!express.enabled}
                  />
                </div>
              </div>
            </section>

            <section style={{ background: '#fff', borderRadius: 16, padding: 18, border: '1px solid rgba(148, 163, 184, 0.35)' }}>
              <h3 style={{ marginBottom: 12 }}>Free shipping</h3>
              <label className="toggle-chip" style={{ display: 'inline-flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
                <input
                  type="checkbox"
                  checked={Boolean(free.enabled)}
                  onChange={(e) => updateShipping('free', { enabled: e.target.checked })}
                />
                Enabled
              </label>

              <div className="form-row" style={{ gap: 14 }}>
                <div className="form-group">
                  <label>Label</label>
                  <input
                    value={free.label || ''}
                    onChange={(e) => updateShipping('free', { label: e.target.value })}
                    placeholder="Free Shipping"
                    disabled={!free.enabled}
                  />
                </div>
                <div className="form-group">
                  <label>Free over (USD)</label>
                  <input
                    value={centsToDollars(free.threshold_cents)}
                    onChange={(e) => updateShipping('free', { threshold_cents: dollarsToCents(e.target.value) ?? free.threshold_cents })}
                    placeholder="20.00"
                    disabled={!free.enabled}
                  />
                </div>
              </div>

              <div className="form-row" style={{ gap: 14 }}>
                <div className="form-group">
                  <label>Min days</label>
                  <input
                    type="number"
                    value={free.min_days ?? 5}
                    onChange={(e) => updateShipping('free', { min_days: Number(e.target.value) })}
                    disabled={!free.enabled}
                  />
                </div>
                <div className="form-group">
                  <label>Max days</label>
                  <input
                    type="number"
                    value={free.max_days ?? 8}
                    onChange={(e) => updateShipping('free', { max_days: Number(e.target.value) })}
                    disabled={!free.enabled}
                  />
                </div>
              </div>
            </section>
          </div>

          </details>

          <details open style={{ marginTop: 22 }}>
            <summary style={{ cursor: 'pointer', fontWeight: 900, fontSize: '1.2rem', color: '#4c1d95' }}>Taxes</summary>
            <section style={{ background: '#fff', borderRadius: 16, padding: 18, border: '1px solid rgba(148, 163, 184, 0.35)', marginTop: 14 }}>
              <label className="toggle-chip" style={{ display: 'inline-flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
                <input
                  type="checkbox"
                  checked={Boolean(form?.tax?.stripe_automatic_tax_enabled)}
                  onChange={(e) => updateTax({ stripe_automatic_tax_enabled: e.target.checked })}
                />
                Enable automatic tax
              </label>
              <p style={{ margin: 0, color: '#475569', fontSize: '0.95rem' }}>
                Requires tax configuration in your payment dashboard (registrations, tax settings).
              </p>
            </section>
          </details>

          <details open style={{ marginTop: 22 }}>
            <summary style={{ cursor: 'pointer', fontWeight: 900, fontSize: '1.2rem', color: '#4c1d95' }}>Optional Sections</summary>

            <div style={{ display: 'grid', gap: 18, marginTop: 14 }}>
              <section style={{ background: '#fff', borderRadius: 16, padding: 18, border: '1px solid rgba(148, 163, 184, 0.35)' }}>
                <h3 style={{ marginBottom: 12 }}>Product features</h3>
                <label className="toggle-chip" style={{ display: 'inline-flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
                  <input
                    type="checkbox"
                    checked={Boolean(form?.optional_sections?.crochet_features?.enabled)}
                    onChange={(e) => updateOptionalCrochetFeatures({ enabled: e.target.checked })}
                  />
                  Enabled
                </label>

                <div className="form-row" style={{ gap: 14 }}>
                  <div className="form-group" style={{ flex: 1 }}>
                    <label>Title</label>
                    <input
                      value={String(form?.optional_sections?.crochet_features?.title || 'Product features')}
                      onChange={(e) => updateOptionalCrochetFeatures({ title: e.target.value })}
                      placeholder="Product features"
                      disabled={!form?.optional_sections?.crochet_features?.enabled}
                    />
                  </div>
                </div>

                <div className="form-row" style={{ gap: 14 }}>
                  <div className="form-group" style={{ flex: 1 }}>
                    <label>HTML content</label>
                    <textarea
                      rows={7}
                      value={String(form?.optional_sections?.crochet_features?.html || '')}
                      onChange={(e) => updateOptionalCrochetFeatures({ html: e.target.value })}
                      placeholder="Add HTML-enabled feature bullets"
                      disabled={!form?.optional_sections?.crochet_features?.enabled}
                      style={{ width: '100%' }}
                    />
                    <div className="field-hint">Rendered on the demo app home page above Events.</div>
                  </div>
                </div>
              </section>

              <section style={{ background: '#fff', borderRadius: 16, padding: 18, border: '1px solid rgba(148, 163, 184, 0.35)' }}>
                <h3 style={{ marginBottom: 12 }}>Events</h3>
                <label className="toggle-chip" style={{ display: 'inline-flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
                  <input
                    type="checkbox"
                    checked={Boolean(form?.optional_sections?.events?.enabled)}
                    onChange={(e) => updateOptionalEvents({ enabled: e.target.checked })}
                  />
                  Enabled
                </label>

                <div className="form-row" style={{ gap: 14 }}>
                  <div className="form-group" style={{ flex: 1 }}>
                    <label>Title</label>
                    <input
                      value={String(form?.optional_sections?.events?.title || 'Events')}
                      onChange={(e) => updateOptionalEvents({ title: e.target.value })}
                      placeholder="Events"
                      disabled={!form?.optional_sections?.events?.enabled}
                    />
                  </div>
                </div>

                <div className="form-row" style={{ gap: 14 }}>
                  <div className="form-group" style={{ flex: 1 }}>
                    <label>HTML content</label>
                    <textarea
                      rows={6}
                      value={String(form?.optional_sections?.events?.html || '')}
                      onChange={(e) => updateOptionalEvents({ html: e.target.value })}
                      placeholder="Paste HTML-enabled events feed content"
                      disabled={!form?.optional_sections?.events?.enabled}
                      style={{ width: '100%' }}
                    />
                    <div className="field-hint">Rendered on the demo app home page below the hero.</div>
                  </div>
                </div>
              </section>

              <section style={{ background: '#fff', borderRadius: 16, padding: 18, border: '1px solid rgba(148, 163, 184, 0.35)' }}>
                <h3 style={{ marginBottom: 12 }}>Reviews</h3>
                <label className="toggle-chip" style={{ display: 'inline-flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
                  <input
                    type="checkbox"
                    checked={Boolean(form?.optional_sections?.reviews?.enabled)}
                    onChange={(e) => updateOptionalReviews({ enabled: e.target.checked })}
                  />
                  Enabled
                </label>

                <div className="form-row" style={{ gap: 14 }}>
                  <div className="form-group" style={{ flex: 1 }}>
                    <label>Title</label>
                    <input
                      value={String(form?.optional_sections?.reviews?.title || 'Reviews')}
                      onChange={(e) => updateOptionalReviews({ title: e.target.value })}
                      placeholder="Reviews"
                      disabled={!form?.optional_sections?.reviews?.enabled}
                    />
                  </div>
                  <div className="form-group" style={{ minWidth: 260 }}>
                    <label>Approval mode</label>
                    <select
                      value={Boolean(form?.optional_sections?.reviews?.auto_approve) ? 'auto' : 'admin'}
                      onChange={(e) => updateOptionalReviews({ auto_approve: e.target.value === 'auto' })}
                      disabled={!form?.optional_sections?.reviews?.enabled}
                    >
                      <option value="admin">Admin approval required</option>
                      <option value="auto">Auto-approve after verification</option>
                    </select>
                    <div className="field-hint">Guests must confirm via email (double opt-in). Logged-in users skip email verification.</div>
                  </div>
                </div>
              </section>
            </div>
          </details>

          <div className="general-actions-bar">
            <button className="btn-primary" onClick={save} disabled={saving}>
              {saving ? 'Saving…' : 'Save'}
            </button>
            <button className="btn-secondary" onClick={fetchSettings} disabled={saving}>
              Refresh
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
